// ResidentIQ controller: same corpus as BoardIQ (POST /search), plus triage,
// ticketing and manager routing. Deliberately has no drafting surface.

import { useCallback, useEffect, useRef, useState } from "react";
import { api } from "./api.js";
import { classify, MANAGER_ROLES } from "./triage.js";

const nextId = (() => { let n = 0; return (p) => p + "_" + (++n) + "_" + Date.now().toString(36); })();
const stamp = () => {
  const d = new Date();
  const p = (x) => String(x).padStart(2, "0");
  return p(d.getHours()) + ":" + p(d.getMinutes()) + ":" + p(d.getSeconds());
};

const emptyManagers = () =>
  MANAGER_ROLES.reduce((acc, m) => { acc[m.id] = ""; return acc; }, {});

/** Mirrors legalcode_search/app/ticketing_client.py's _requester_identifier()
 * exactly, so an edit/comment call is attributed to the same identifier a
 * ticket was created and looked up under (email, else "name · Unit X"). */
const requesterIdentifier = (resident) => {
  const email = resident && resident.email;
  if (email && email.trim()) return email.trim().toLowerCase();
  const name = (resident && resident.name) || "Resident";
  const unit = resident && resident.unit;
  return unit ? name + " · Unit " + unit : name;
};

// `sessionResident` is the resident record POST /communities/select already
// resolved at login (see session.js) -- real name/email from the
// community's directory, not the free-form profile a resident used to
// have to type into Settings. It seeds/overrides that profile's
// name/email below so ticket creation and the dashboard both key off a
// stable identity from the moment a resident signs in, and it's also
// what the dashboard itself is fetched for (see loadDashboard()).
export function useResidentIQ({ communityName, sessionResident } = {}) {
  // ── ResidentIQ chat (read-only corpus, no resolution drafting) ────────
  const [messages, setMessages] = useState([]);
  const [pending, setPending] = useState(false);
  const [debugLines, setDebugLines] = useState([]);
  const abortRef = useRef(null);

  const pushDebug = useCallback((line, kind = "info") => {
    setDebugLines((prev) => [...prev.slice(-60), { id: nextId("dbg"), at: stamp(), line, kind }]);
  }, []);

  // ── resident identity (from the signed-in session, or the older free-form
  // profile as a fallback) ─────────────────────────────────────────────
  const [resident, setResident] = useState(() => {
    let draft = {};
    try { draft = JSON.parse(localStorage.getItem("hoa-resident") || "{}"); } catch { /* ignore */ }
    // A signed-in resident's real name/email/id (from the login directory
    // lookup) always wins over whatever's in the older free-form profile --
    // that profile is now only a fallback for a deployment/tab that never
    // went through the email-login flow.
    return sessionResident
      ? { ...draft, id: sessionResident.id, name: sessionResident.name || draft.name, email: sessionResident.email || draft.email }
      : draft;
  });

  // Keeps `resident` in sync if the session's resident record changes
  // after mount (e.g. a slow login round-trip that resolves after this
  // hook's first render).
  useEffect(() => {
    if (!sessionResident) return;
    setResident((prev) => ({
      ...prev, id: sessionResident.id,
      name: sessionResident.name || prev.name, email: sessionResident.email || prev.email,
    }));
  }, [sessionResident]);

  const saveResident = useCallback((next) => {
    setResident(next);
    try { localStorage.setItem("hoa-resident", JSON.stringify(next)); } catch { /* ignore */ }
  }, []);

  const ask = useCallback(async (query) => {
    const text = String(query || "").trim();
    if (!text || pending) return "";

    const history = messages
      .filter((m) => m.role === "user" || m.role === "assistant")
      .slice(-8)
      .map((m) => ({ role: m.role, content: m.text }));

    setMessages((prev) => [...prev, { id: nextId("u"), role: "user", text }]);
    setPending(true);
    pushDebug("POST " + api.base + "/search · agent=residentiq · simplify=true", "req");

    const ctrl = new AbortController();
    abortRef.current = ctrl;

    try {
      // Same knowledge base as BoardIQ; simplify=true for resident-facing plain language.
      const res = await api.search({ query: text, scope: "all", simplify: true, knowledgeMode: "grounded", history }, ctrl.signal);
      const answer = res.answer || res.synthesis || res.reply || "";
      pushDebug("200 · " + ((res.citations || res.results || []).length) + " passages", "ok");
      // Fire-and-forget: powers the dashboard's "recent questions" list
      // (see resident_dashboard.py's resident_queries collection). Never
      // blocks or fails the chat turn itself -- a resident without a
      // resolved email yet (session still loading) just doesn't get this
      // one logged.
      if (resident && resident.email) {
        api.logResidentQuery(resident.email, text).catch(() => {});
      }
      setMessages((prev) => [...prev, {
        id: nextId("a"),
        role: "assistant",
        text: answer,
        sources: (res.citations || res.results || []).map((c, i) => ({
          id: nextId("src"),
          label: c.label || c.section || c.source || ("§" + (i + 1)),
          text: c.text || c.snippet || "",
          document: c.document || c.filename || "",
        })),
        unverified: !!res.unverified,
      }]);
      return answer;
    } catch (err) {
      if (err.name === "AbortError") return "";
      pushDebug((err.status || "ERR") + " · " + err.message, "err");
      setMessages((prev) => [...prev, { id: nextId("e"), role: "error", text: err.message }]);
      return "";
    } finally {
      abortRef.current = null;
      setPending(false);
    }
  }, [messages, pending, pushDebug, resident]);

  const stop = useCallback(() => { abortRef.current && abortRef.current.abort(); }, []);
  const clearChat = useCallback(() => { setMessages([]); setDebugLines([]); }, []);

  // ── manager contacts ─────────────────────────────────────────────────
  const [managers, setManagers] = useState(emptyManagers);

  const loadManagers = useCallback(async () => {
    try {
      const res = await api.getManagers();
      setManagers({ ...emptyManagers(), ...(res.managers || res || {}) });
    } catch {
      // Endpoint not deployed yet — fall back to whatever the device remembers.
      try {
        const raw = localStorage.getItem("hoa-managers");
        if (raw) setManagers({ ...emptyManagers(), ...JSON.parse(raw) });
      } catch { /* ignore */ }
    }
  }, []);

  const saveManagers = useCallback(async (next) => {
    setManagers(next);
    try { localStorage.setItem("hoa-managers", JSON.stringify(next)); } catch { /* ignore */ }
    try { await api.setManagers(next); } catch { /* server route optional for now */ }
  }, []);

  useEffect(() => { loadManagers(); }, [loadManagers]);

  // ── triage → answer → ticket + email ─────────────────────────────────
  const [intake, setIntake] = useState({
    stage: "idle",        // idle | triaging | review | submitting | submitted
    description: "",
    triage: null,         // {category, area, confidence, reasons}
    answer: "",
    sources: [],
    ticket: null,
    error: "",
    routedBySystem: true, // false once the resident overrides the area
  });

  /** Step 1: classify the free-text description, then answer it from the corpus. */
  const describe = useCallback(async (description) => {
    const text = String(description || "").trim();
    if (!text) return;

    setIntake((p) => ({ ...p, stage: "triaging", description: text, error: "", ticket: null }));
    pushDebug("POST " + api.base + "/resident/triage", "req");

    let triage;
    try {
      const res = await api.triageRequest({ description: text });
      triage = {
        category: res.category,
        area: res.area || res.manager || res.route,
        confidence: res.confidence ?? 0.9,
        reasons: res.reasons || [],
      };
      pushDebug("200 · " + triage.category + " → " + triage.area, "ok");
    } catch (err) {
      triage = classify(text);
      pushDebug("triage unavailable (" + (err.status || "network") + ") · classified on device: " + triage.category + " → " + triage.area, "info");
    }

    // The question gets answered whatever the category is.
    let answer = "";
    let sources = [];
    try {
      pushDebug("POST " + api.base + "/search · answering the request", "req");
      const res = await api.search({ query: text, scope: "all", simplify: true, knowledgeMode: "hybrid" });
      answer = res.answer || res.synthesis || res.reply || "";
      sources = (res.citations || res.results || []).map((c, i) => ({
        id: nextId("src"),
        label: c.label || c.section || c.source || ("§" + (i + 1)),
        text: c.text || c.snippet || "",
        document: c.document || c.filename || "",
      }));
      pushDebug("200 · " + sources.length + " passages cited", "ok");
    } catch (err) {
      pushDebug("answer unavailable · " + err.message, "err");
    }

    // The ticket defaults to the community admin (Association Manager) as
    // its owner regardless of the classified subject area -- the resident
    // can still hand-route it to a specific manager below (overrideArea),
    // but nothing is auto-routed to a vendor-specific inbox on their behalf.
    const routedTriage = { ...triage, area: "association_manager" };

    setIntake((p) => ({ ...p, stage: "review", triage: routedTriage, answer, sources, routedBySystem: true }));
  }, [communityName, resident, pushDebug]);

  /** Resident hand-routes the ticket to a different manager than the community-admin default. */
  const overrideArea = useCallback((area) => {
    setIntake((p) => {
      if (!p.triage) return p;
      return { ...p, triage: { ...p.triage, area }, routedBySystem: false };
    });
  }, []);

  const overrideCategory = useCallback((category) => {
    setIntake((p) => {
      if (!p.triage) return p;
      return { ...p, triage: { ...p.triage, category } };
    });
  }, []);

  // ── tickets ──────────────────────────────────────────────────────────
  const [tickets, setTickets] = useState([]);

  const refreshTickets = useCallback(async () => {
    // Scoped by the resident's email once known -- GET /resident/requests
    // returns an empty list for an unscoped call (see
    // ticketing_client.py's list_resident_requests), so an unresolved
    // identity just means an empty "My requests" list, not a request for
    // someone else's tickets.
    if (!resident || !resident.email) { setTickets([]); return; }
    try {
      const res = await api.listResidentRequests(resident.email);
      setTickets(res.requests || res.tickets || []);
    } catch { /* ticketing service unreachable — the session's own tickets stay listed */ }
  }, [resident]);

  useEffect(() => { refreshTickets(); }, [refreshTickets]);

  // ── ticket detail (click-through from the list) ───────────────────────
  const [selectedTicketId, setSelectedTicketId] = useState(null);
  const [ticketDetail, setTicketDetail] = useState(null);
  const [ticketDetailStatus, setTicketDetailStatus] = useState("idle"); // idle | loading | ready | error
  const [ticketDetailError, setTicketDetailError] = useState("");

  const loadTicketDetail = useCallback(async (id) => {
    setTicketDetailStatus("loading");
    setTicketDetailError("");
    try {
      const ticket = await api.getResidentRequest(id);
      setTicketDetail(ticket);
      setTicketDetailStatus("ready");
    } catch (err) {
      setTicketDetailError(err?.message || "Couldn't load this request.");
      setTicketDetailStatus("error");
    }
  }, []);

  const selectTicket = useCallback((id) => {
    setSelectedTicketId(id);
    loadTicketDetail(id);
  }, [loadTicketDetail]);

  const clearSelectedTicket = useCallback(() => {
    setSelectedTicketId(null);
    setTicketDetail(null);
    setTicketDetailStatus("idle");
    setTicketDetailError("");
  }, []);

  /** Originator-only: revise the free-text description. The subject is
   * always re-derived server-side from the new text. */
  const editTicket = useCallback(async (description) => {
    if (!selectedTicketId) return;
    const ticket = await api.editResidentRequest(selectedTicketId, {
      actorIdentifier: requesterIdentifier(resident),
      description,
    });
    setTicketDetail(ticket);
    setTickets((prev) => prev.map((t) => (t.id === ticket.id ? ticket : t)));
    return ticket;
  }, [selectedTicketId, resident]);

  /** Add a time-stamped comment as the signed-in resident (the ticket's
   * originator). A future board/admin surface would pass actorRole
   * "board"/"admin" instead -- the backend already supports both. */
  const addTicketComment = useCallback(async (text) => {
    if (!selectedTicketId) return;
    const t = String(text || "").trim();
    if (!t) return;
    const ticket = await api.addResidentRequestComment(selectedTicketId, {
      actorIdentifier: requesterIdentifier(resident),
      actorRole: "resident",
      text: t,
    });
    setTicketDetail(ticket);
    setTickets((prev) => prev.map((x) => (x.id === ticket.id ? ticket : x)));
    return ticket;
  }, [selectedTicketId, resident]);

  // ── dashboard (see legalcode_search/app/resident_dashboard.py) ────────
  // Everything ResidentDashboard.jsx renders in one round-trip: the
  // name/email/unit header, open/pending/closed counts, recent requests,
  // recent questions, last communication, last payment, and this
  // community's announcements. Loaded once a real resident id is known
  // (i.e. once the signed-in session's directory lookup has resolved),
  // and re-loadable on demand (e.g. a "Refresh" button, or after
  // submitting a new request).
  const [dashboard, setDashboard] = useState(null);
  const [dashboardStatus, setDashboardStatus] = useState("idle"); // idle | loading | ready | error
  const [dashboardError, setDashboardError] = useState("");

  const loadDashboard = useCallback(async () => {
    if (!resident || !resident.id) return;
    setDashboardStatus("loading");
    setDashboardError("");
    try {
      const res = await api.getResidentDashboard(resident.id);
      setDashboard(res);
      setDashboardStatus("ready");
    } catch (err) {
      setDashboardError(err?.message || "Couldn't load your dashboard.");
      setDashboardStatus("error");
    }
  }, [resident]);

  useEffect(() => { loadDashboard(); }, [loadDashboard]);

  /** Step 2: confirmed -- save the ticket into the ticketing database. */
  const submit = useCallback(async () => {
    setIntake((p) => ({ ...p, stage: "submitting", error: "" }));
    const snapshot = intake;

    pushDebug("POST " + api.base + "/resident/requests · owner=" + snapshot.triage.area, "req");
    try {
      const res = await api.createResidentRequest({
        description: snapshot.description,
        category: snapshot.triage.category,
        area: snapshot.triage.area,
        answer: snapshot.answer,
        resident,
      });
      const ticket = res.ticket || res;
      pushDebug("201 · ticket " + (ticket.number || ticket.id) + " saved", "ok");
      setIntake((p) => ({ ...p, stage: "submitted", ticket }));
      setTickets((prev) => [ticket, ...prev]);
      loadDashboard(); // refresh counts/recent requests now that a new one exists
    } catch (err) {
      pushDebug((err.status || "ERR") + " · " + err.message, "err");
      setIntake((p) => ({ ...p, stage: "review", error: err.message }));
    }
  }, [intake, resident, pushDebug, loadDashboard]);

  const resetIntake = useCallback(() => setIntake({
    stage: "idle", description: "", triage: null, answer: "", sources: [],
    ticket: null, error: "", routedBySystem: true,
  }), []);

  return {
    // chat
    messages, pending, ask, stop, clearChat, debugLines,
    // intake
    intake, describe, submit, resetIntake, overrideArea, overrideCategory,
    // identity + routing
    resident, saveResident, managers, saveManagers, loadManagers,
    // tickets
    tickets, refreshTickets,
    // ticket detail
    selectedTicketId, ticketDetail, ticketDetailStatus, ticketDetailError,
    selectTicket, clearSelectedTicket, editTicket, addTicketComment, requesterIdentifier,
    // dashboard
    dashboard, dashboardStatus, dashboardError, loadDashboard,
  };
}
